import { FeedbacksTable } from '@/components/feedbacks/feedbacks-table.js';
import { InfoPage } from '@/components/info-page.js';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown.js';
import { TableFilterInputField } from '@/components/table/table-filter-field.js';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button.js';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu.js';
import { buildNameLikeSelectOptions } from '@/components/ui/multi-select-utils.js';
import { lessonTypeaheadSearch, useAllSchools, useLessonById } from '@/lib/api-call-hooks.js';
import { EmptyArray, getSessionFeedbacks, getUser } from '@/lib/api-calls.js';
import { useRowsBulkSelection } from '@/lib/bulk-selection.js';
import { usePaginatedFilters } from '@/lib/filtering.js';
import { paginationRequestSchema } from '@/lib/pagination.js';
import { QueryKeys } from '@/lib/queries.js';
import { BulkUpdateLateFeedback } from '@/routes/_layout/sessions/-components/bulk-update-late-feedback.js';
import { BulkUpdateStudentAttendance } from '@/routes/_layout/sessions/-components/bulk-update-student-attended.js';
import { distinct, isAirAdmin, isNotNullish, PrismaEnums } from '@airreading/common';
import {
  GetSessionFeedbacksModifier,
  lessonKindSchema,
  LessonKindToLabel,
  SessionFeedbackWithAssessment,
  studentYearOption,
  toSessionFeedbackCompoundId,
} from '@airreading/common/data';
import { useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { z } from 'zod';
import { coachesTabs } from '../-components/tabs.js';
import { validateRouteRole } from '@/lib/routing.js';

export const Route = createFileRoute('/_layout/teachers/$teacherId/feedbacks')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: TeacherFeedbacks,
  validateSearch: paginationRequestSchema.extend({
    feedbackSubmitted: z.boolean().optional(),
    lessonIds: z.string().array().optional(),
    grades: z.array(z.nativeEnum(PrismaEnums.StudentYear)).optional(),
    query: z.string().optional(),
    schoolIds: z.string().array().optional(),
    orgIds: z.string().array().optional(),
    sessionPlainDateInUSCentral: z.string().optional(),
    lessonKinds: z.array(lessonKindSchema).optional(),
  }),
});

function TeacherFeedbacks() {
  const { teacherId } = Route.useParams();
  const { data: user } = useSuspenseQuery({
    queryKey: QueryKeys.getUser(teacherId),
    queryFn: () => getUser(teacherId),
  });

  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[query, activeQuery], setQuery],
      grades: [[grades, activeGrades], setGrades],
      feedbackSubmitted: [[feedbackSubmitted, activeFeedbackSubmitted], setFeedbackSubmitted],
      lessonIds: [[selectedLessonIds, activeLessonIds], setLessonIds],
      schoolIds: [[schoolIds, activeSchoolIds], setSchoolIds],
      orgIds: [[orgIds, activeOrgIds], setOrgIds],
      lessonKinds: [[selectedLessonKinds, activeSelectedLessonKinds], setSelectedLessonKinds],
    },
    resetFilters,
  } = usePaginatedFilters(Route.id, [
    'query',
    'grades',
    'feedbackSubmitted',
    'lessonIds',
    'schoolIds',
    'orgIds',
    'sessionPlainDateInUSCentral',
    'lessonKinds',
  ]);

  const requestQuery = {
    pageIndex,
    pageSize,
    filters: {
      feedbackSubmitted: activeFeedbackSubmitted,
      coachId: teacherId, // Fixed parameter from route
      lessonId: activeLessonIds?.[0],
      query: activeQuery,
      grades: activeGrades,
      schoolIds: activeSchoolIds,
      orgIds: activeOrgIds,
      lessonKinds: activeSelectedLessonKinds,
    },
  } satisfies GetSessionFeedbacksModifier;

  const { data: allSchools, isPending: isLoadingSchools } = useAllSchools();
  const allSchoolOptions = allSchools.map(buildNameLikeSelectOptions);
  const allOrgOptions = allSchools
    .map((school) => school.org)
    .filter(distinct((o) => o.id))
    .map(buildNameLikeSelectOptions);

  const {
    data: sessionFeedbacksResp,
    isPending: isLoadingSessionFeedbacks,
    error,
  } = useQuery(getSessionFeedbacks(requestQuery));

  const sessionFeedbacks = useMemo(() => sessionFeedbacksResp?.sessionFeedbacks || EmptyArray, [sessionFeedbacksResp]);
  const queryClient = useQueryClient();

  const { rowSelection, rowDataSelection, onRowSelectionChange, getRowId, resetAllRowSelection } =
    useRowsBulkSelection<SessionFeedbackWithAssessment>({
      getRowId: (row) => toSessionFeedbackCompoundId(row),
      rowIdToData: (id) => sessionFeedbacks.find((feedback) => toSessionFeedbackCompoundId(feedback) === id),
    });

  const fetchLessonById = useLessonById();

  const filterLeftComponents = useMemo(
    () => (
      <>
        <TableFilterInputField
          placeholder="Search sessions by student name or student email"
          value={query ?? ''}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
        />
        <TableFilterDropdown
          title="Feedback submitted?"
          singleSelect
          options={[
            { value: true, label: 'Submitted' },
            { value: false, label: 'Not submitted' },
          ]}
          selectedValues={[feedbackSubmitted].filter(isNotNullish)}
          onChange={(selected) => {
            setFeedbackSubmitted(selected.filter((v) => v !== feedbackSubmitted)[0]);
          }}
        />
        <TableFilterDropdown
          title="District"
          options={allOrgOptions}
          selectedValues={orgIds ?? []}
          onChange={setOrgIds}
          isLoading={isLoadingSchools}
        />
        <TableFilterDropdown
          title="School"
          options={allSchoolOptions}
          selectedValues={schoolIds ?? []}
          onChange={setSchoolIds}
          isLoading={isLoadingSchools}
        />
        <TableFilterDropdown
          title="Lesson name"
          options={lessonTypeaheadSearch(queryClient)}
          selectedValues={selectedLessonIds ?? []}
          onChange={setLessonIds}
          singleSelect
          getOptionSearchFn={fetchLessonById}
        />
        <TableFilterDropdown
          title="Grade"
          options={Object.values(PrismaEnums.StudentYear).map((grade) => ({
            label: studentYearOption(grade),
            value: grade,
          }))}
          selectedValues={grades ?? []}
          onChange={setGrades}
        />
        <TableFilterDropdown
          title="Assessment Lesson"
          options={lessonKindSchema.options
            .filter((type) => type !== 'practice')
            .map((type) => ({
              label: LessonKindToLabel[type],
              value: type,
            }))}
          selectedValues={selectedLessonKinds ?? []}
          onChange={setSelectedLessonKinds}
        />
        <TableFilterResetButton show onClick={resetFilters} />
      </>
    ),
    [
      query,
      feedbackSubmitted,
      allOrgOptions,
      orgIds,
      setOrgIds,
      isLoadingSchools,
      allSchoolOptions,
      schoolIds,
      setSchoolIds,
      queryClient,
      selectedLessonIds,
      setLessonIds,
      fetchLessonById,
      grades,
      setGrades,
      resetFilters,
      setQuery,
      setFeedbackSubmitted,
    ],
  );

  if (error) {
    return (
      <InfoPage
        headerText={user.fullNameOrEmail}
        tabs={coachesTabs({ teacherId })}
        breadcrumbs={{
          linkItems: [
            {
              label: 'Teachers',
              link: {
                to: '/teachers',
              },
            },
          ],
          currentPage: user.fullNameOrEmail ?? '',
        }}
      >
        <div>Failed to fetch teacher feedback entries. Please try again or notify Air Reading.</div>
      </InfoPage>
    );
  }

  return (
    <InfoPage
      headerText={user.fullNameOrEmail}
      loading={isLoadingSessionFeedbacks}
      tabs={coachesTabs({ teacherId })}
      breadcrumbs={{
        linkItems: [
          {
            label: 'Teachers',
            link: {
              to: '/teachers',
            },
          },
        ],
        currentPage: user.fullNameOrEmail ?? '',
      }}
    >
      <FeedbacksTable
        from="teacher"
        sessionFeedbacks={sessionFeedbacks}
        tableHookProps={{
          state: {
            rowSelection,
            pagination: {
              pageIndex,
              pageSize,
            },
          },
          onPaginationChange,
          onRowSelectionChange,
          getRowId,
          rowCount: sessionFeedbacksResp?.total || 0,
        }}
        filters={{
          filterLeftComponents,
        }}
        bulkActionsMenuItems={
          <>
            <BulkUpdateStudentAttendance
              key={'cancel-attendance'}
              selectedFeedbacks={rowDataSelection}
              trigger={
                <DropdownMenuItem onSelect={(e) => e.preventDefault()}>Cancel student attendance</DropdownMenuItem>
              }
              attended={false}
              onSuccess={resetAllRowSelection}
            />
            <BulkUpdateStudentAttendance
              key={'mark-attendance'}
              selectedFeedbacks={rowDataSelection}
              trigger={
                <DropdownMenuItem onSelect={(e) => e.preventDefault()}>Mark student as attended</DropdownMenuItem>
              }
              attended={true}
              onSuccess={resetAllRowSelection}
            />
            <BulkUpdateLateFeedback
              lateFeedbackDismissed={true}
              trigger={
                <DropdownMenuItem onSelect={(e) => e.preventDefault()}>Dismiss late feedback tag</DropdownMenuItem>
              }
              selectedFeedbacks={rowDataSelection}
              onSuccess={resetAllRowSelection}
            />
          </>
        }
      />
    </InfoPage>
  );
}
